import React, { useEffect, useRef, useState, useMemo } from "react";
import serviceApi from "../../../services/serviceApi";
import environment from "../../../environments/environment";

type DiaryEntry = {
    id: string;
    title: string;
    date: string;
    classWork: string;
    homeWork: string;
    cwAttachments?: string[];
    hwAttachments?: string[];
};

type DiaryResponse = {
    status: string;
    dataList: DiaryEntry[];
};
type Day = {
    date: string;
    dayName: string;
    fullDate: string;
};

const StudentDiary: React.FC = () => {

    const today = new Date();
    const initialDateStr = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;

    const [selectedDate, setSelectedDate] = useState(initialDateStr);
    const [loading, setLoading] = useState(false);
    const [diaryEntries, setDiaryEntries] = useState<DiaryEntry[]>([]);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    const calendarRef = useRef<HTMLInputElement>(null);
    const activeDateRef = useRef<HTMLDivElement | null>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const dateRefs = useRef<Record<string, HTMLDivElement | null>>({});

    const monthDays = useMemo(() => {
        const [d, m, y] = selectedDate.split("/").map(Number);
        const daysInMonth = new Date(y, m, 0).getDate();
        const daysArray: Day[] = [];
        for (let i = 1; i <= daysInMonth; i++) {
            const dateObj = new Date(y, m - 1, i);
            daysArray.push({
                date: String(i).padStart(2, '0'),
                dayName: dateObj.toLocaleDateString("en-US", { weekday: "short" }),
                fullDate: `${String(i).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`
            });
        }
        return daysArray;
    }, [selectedDate]);

    const dateParts = selectedDate.split("/");
    const displayMonth = new Date(+dateParts[2], +dateParts[1] - 1).toLocaleString('default', { month: 'short' });
    const displayYear = dateParts[2];

    useEffect(() => {
        fetchDiary(selectedDate);
    }, [selectedDate]);

    const fetchDiary = async (date: string) => {
        setLoading(true);
        setDiaryEntries([]);

        try {
            const res = await serviceApi.get<DiaryResponse>(
                `${environment.studentDiary}?date=${date}`
            );

            if (res.status === "SUCCESS" && Array.isArray(res.dataList)) {
                const filtered = res.dataList.filter(item => item.date === date);
                setDiaryEntries(filtered);
            } else {
                setDiaryEntries([]);
            }
        } catch (err) {
            console.error(err);
            setDiaryEntries([]);
        } finally {
            setLoading(false);
        }
    };

    const onCalendarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (!val) return;
        const formatted = val.split("-").reverse().join("/");
        setSelectedDate(formatted);
        // fetchDiary(formatted);
    };

    useEffect(() => {
        const activeElement = dateRefs.current[selectedDate];

        if (activeElement) {
            activeElement.scrollIntoView({
                behavior: "smooth",
                inline: "center",
                block: "nearest",
            });
        }
    }, [selectedDate]);

    const handleDateSelect = (date: string) => {
        setSelectedDate(date);
        fetchDiary(date);
    };

    return (
        <div className="notification-page" style={{ padding: "0 var(--sz-15)", textAlign: 'left' }}>
            <h2 className="title-text notification-heading" style={{ margin: "var(--sz-20) 0" }}>
                Student Diary
            </h2>
            {loading && (
                <div className="loading-overlay">
                    <div className="loading-overlay__content">
                        <div className="spinner spinner--lg"></div>
                        <span className="loading-text">Loading Diary...</span>
                    </div>
                </div>
            )}
            <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '20px',
                alignItems: 'stretch',
                width: '100%'
            }}>
                <div
                    onClick={() => calendarRef.current?.showPicker()}
                    style={{
                        backgroundColor: 'var(--colorPrimaryDark)',
                        color: 'white',
                        padding: '10px',
                        borderRadius: '8px',
                        textAlign: 'center',
                        minWidth: '80px',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        position: 'relative'
                    }}
                >
                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{displayMonth}</div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{displayYear}</div>
                    <input
                        ref={calendarRef}
                        type="date"
                        style={{ position: 'absolute', opacity: 0, inset: 0 }}
                        onChange={onCalendarSelect}
                    />
                </div>
                <div
                    ref={scrollRef}
                    style={{
                        display: 'flex',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        gap: '10px',
                        flex: 1,
                        paddingBottom: '5px',
                        scrollBehavior: 'smooth'
                    }}
                >
                    {monthDays.map((d, i) => {
                        const isActive = selectedDate === d.fullDate;
                        return (
                            <div
                                key={i}
                                ref={(el) => {
                                    dateRefs.current[d.fullDate] = el;
                                }}
                                onClick={() => handleDateSelect(d.fullDate)}
                                style={{
                                    background: isActive ? '#fff' : '#f8f8f8',
                                    padding: '10px',
                                    borderRadius: '10px',
                                    border: isActive
                                        ? '2px solid var(--colorAccent)'
                                        : '1px solid #eee',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    minWidth: '60px',
                                    flexShrink: 0
                                }}
                            >
                                <div style={{
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    color: isActive ? 'var(--colorAccentDark)' : '#e0544d'
                                }}>
                                    {d.date}
                                </div>
                                <div style={{ fontSize: '11px', color: '#666' }}>
                                    {d.dayName}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {diaryEntries.length > 0 ? diaryEntries.map((entry) => (
                    <div key={entry.id} className="event-container" style={{ margin: 0 }}>
                        <div className="events-title colorOrange" style={{ padding: '10px 15px' }}>
                            {entry.title}
                        </div>
                        <div style={{ padding: '10px 15px' }}>
                            <div style={{ fontWeight: 'bold', color: 'var(--colorPrimaryDark)' }}>Homework</div>
                            <div>{entry.homeWork || "---"}</div>
                        </div>
                        <div className="horizontal-divider" style={{ margin: "6px 0" }} />
                        <div style={{ padding: '10px 15px' }}>
                            <div style={{ fontWeight: 'bold', color: 'var(--colorPrimaryDark)' }}>Classwork</div>
                            <div>{entry.classWork || "---"}</div>
                        </div>
                    </div>
                )) : (
                    <div className="event-container" style={{
                        padding: '15px',
                        textAlign: 'center',
                        width: 'auto',
                        alignSelf: 'center'
                    }}>
                        <p style={{ margin: 0, color: '#999' }}>
                            No diary records found for {selectedDate}
                        </p>
                    </div>
                )}
            </div>
            {selectedImage && (
                <div className="popup-overlay" onClick={() => setSelectedImage(null)}>
                    <div className="popup-box" onClick={e => e.stopPropagation()}>
                        <img src={selectedImage} style={{ width: '100%' }} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudentDiary;